import {
  useRef,
  Children,
  cloneElement,
  isValidElement,
  ReactNode,
  useCallback,
  MouseEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentSite } from '@hotjar/site';
import styled from '@emotion/styled';
import { Tooltip, tokens } from '@hotjar/hotsauce';
import { WidgetGhostButton } from '@hotjar/webapp/features/Activation/SpaceOverview/components/WidgetGhostButton';
import { QueryFormatFilter } from '@hotjar/global-filters';
import { COUNTRIES, CountryFlag } from '@hotjar/country-lib';
import { StyledRecordingTileFlag } from '../../../components/common/RecordingTile/styles';

import type { Recording } from '@hotjar/webapp/domains/recordings/types';

import { onRecordingsClick } from '../utils/onRecordingsClick';
import { OverviewTrackingElements } from '../../RecordingsPage/utils';
import { RecordingOverviewWidgetCellDuration as CellDuration } from './cells/RecordingOverviewWidgetCellDuration';

const RecordingsList = styled.ul`
  display: flex;
  flex-direction: column;
  width: 100%;
  list-styled: none;
  padding: 0;
  margin: 0;
`;

const RecordingItem = styled.li`
  display: grid;
  grid-template-columns: auto 1fr 1fr 1fr auto;
  align-items: center;
  padding: ${tokens.spacer.x1pt5} ${tokens.spacer.x2};
  box-shadow: inset 0px -1px 0px ${tokens.color.neutral_100};

  &:hover {
    cursor: pointer;
    background-color: ${tokens.color.bg_hover};
    transition: background-color 300ms linear;
  }

  &:last-of-type {
    border-bottom: 0;
  }
`;

const Cell = styled.div`
  text-align: center;
  ${tokens.text('caption')};
  &:first-of-type {
    text-align: left;
  }
  &:last-of-type {
    text-align: right;
  }
`;

const ListFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: ${tokens.spacer.x0pt5} ${tokens.spacer.x1pt5};

  &:hover {
    cursor: pointer;
    background-color: ${tokens.color.bg_hover};
    transition: background-color 300ms linear;
  }
`;

interface RecordingsCardListProps {
  recordingsList: Recording[];
  sortBy: string;
  filters: QueryFormatFilter;
  count: number;
  onPlayClick: (renderPosition: number, elementName: string) => void;
  onViewAllClick: (elementName: string) => void;
  children: ReactNode;
}

const countryCodeToName: Record<string, string> = COUNTRIES.reduce(
  (acc: Record<string, string>, country: { value: string; label: string }) => {
    acc[country.value] = country.label;
    return acc;
  },
  {},
);

const getCountryName = (recording: Recording): string | undefined =>
  countryCodeToName[recording.countryCode];

export const RecordingsOverviewCardList = ({
  recordingsList,
  sortBy,
  filters,
  count,
  onPlayClick,
  onViewAllClick,
  children,
}: RecordingsCardListProps): JSX.Element | null => {
  const { t } = useTranslation();
  const { data: currentSite } = useCurrentSite();
  const currentTimestamp = Math.floor(Date.now() / 1000);
  const amountOfDaysInCreatedFilter = sortBy === '-relevance_score' ? 1 : 7;
  // Ideally we'd like to store info about the fetch time of each recordings list, but this seems like an overkill considering that it's currently a disabled experiment
  const daysAgoTimestamp = currentTimestamp - amountOfDaysInCreatedFilter * 24 * 60 * 60;
  const recordingsRequestTimeRangeRef = useRef([daysAgoTimestamp, currentTimestamp]);

  const onRowClick = useCallback(
    (index: number, recording: Recording, elementName: string, e?: MouseEvent) => {
      // Prevent callback from beinged triggered on parent element as well
      e && e.preventDefault();

      const renderPosition = index + 1;
      onPlayClick(renderPosition, elementName);
      onRecordingsClick(
        recording,
        recordingsList,
        filters,
        sortBy,
        recordingsRequestTimeRangeRef.current,
      );
    },
    [filters, onPlayClick, recordingsList, sortBy],
  );

  const onFooterClick = useCallback(
    async (e?: MouseEvent) => {
      // Prevent callback from beinged triggered on parent element as well
      e && e.stopPropagation();

      onViewAllClick(OverviewTrackingElements.WIDGET_FOOTER_BUTTON);
    },
    [onViewAllClick],
  );

  if (!currentSite?.id) {
    return null;
  }

  return (
    <>
      <RecordingsList>
        {recordingsList.slice(0, 3).map((recording, index) => (
          <RecordingItem
            key={recording.id}
            onClick={() => onRowClick(index + 1, recording, OverviewTrackingElements.WIDGET_ROW)}
          >
            {Children.map(children, (child) => {
              if (isValidElement(child)) {
                return cloneElement(child as React.ReactElement, {
                  recording,
                });
              }
              return child;
            })}

            <Cell>
              <Tooltip
                text={getCountryName(recording) ?? t('common:recordings.recording-tile.country')}
              >
                <StyledRecordingTileFlag>
                  <CountryFlag code={recording.countryCode} />
                </StyledRecordingTileFlag>
              </Tooltip>
            </Cell>

            <CellDuration recording={recording} />

            <Cell>
              <WidgetGhostButton
                data-testid={`cta_${recording.id}`}
                size="s"
                icon="play_alt"
                onClick={(e: MouseEvent) =>
                  onRowClick(
                    index + 1,
                    recording,
                    OverviewTrackingElements.WIDGET_ROW_PLAY_BUTTON,
                    e,
                  )
                }
              >
                {`${t('common:recordings.recordings-overview.play')}`}
              </WidgetGhostButton>
            </Cell>
          </RecordingItem>
        ))}
      </RecordingsList>
      <ListFooter onClick={() => onFooterClick()}>
        <WidgetGhostButton onClick={(e: MouseEvent) => onFooterClick(e)}>
          {t('common:recordings.recordings-overview.view-recordings', {
            count,
          })}
        </WidgetGhostButton>
      </ListFooter>
    </>
  );
};
